import { Hand, X, MapPin, Clock, Info, Check, Ban } from 'lucide-react';
import { Mission } from '../data/missions';

interface RequestedStopsViewProps {
    stopRequests: { station_name: string; status: string }[];
    activeMission: Mission;
    onClose: () => void;
    onAccept: (stationName: string) => Promise<void>;
    onReject: (stationName: string) => Promise<void>;
}

export default function RequestedStopsView({
    stopRequests,
    activeMission,
    onClose,
    onAccept,
    onReject
}: RequestedStopsViewProps) {

    // Associer les noms d'arrêts demandés aux données complètes de la mission
    const detailedStops = activeMission.stops
        .filter(stop =>
            stopRequests.some(r => r.station_name.trim().toLowerCase() === stop.stationName.trim().toLowerCase())
        )
        .sort((a, b) => a.pk - b.pk); // Ordonner par PK

    return (
        <div className="absolute inset-0 bg-[#020617] z-[5000] flex flex-col text-white animate-in fade-in slide-in-from-bottom-6 duration-300">
            {/* HEADER */}
            <header className="px-6 py-6 bg-slate-900/50 backdrop-blur-md border-b border-white/5 flex justify-between items-center shrink-0">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center border border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.2)]">
                        <Hand className="text-blue-400 animate-pulse" size={24} />
                    </div>
                    <div>
                        <h2 className="text-lg font-black uppercase leading-none tracking-tight">Arrêts Demandés</h2>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">
                            {detailedStops.length} arrêt{detailedStops.length > 1 ? 's' : ''} en attente
                        </p>
                    </div>
                </div>
                <button
                    onClick={onClose}
                    className="p-3 bg-white/5 hover:bg-white/10 rounded-2xl text-slate-400 border border-white/10 active:scale-95 transition-all"
                >
                    <X size={20} />
                </button>
            </header>

            {/* CONTENT */}
            <main className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                {detailedStops.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-center opacity-40 px-10">
                        <div className="w-20 h-20 bg-slate-800/50 rounded-full flex items-center justify-center mb-6">
                            <Info size={40} className="text-slate-600" />
                        </div>
                        <p className="text-lg font-bold uppercase tracking-tight">Aucun arrêt demandé</p>
                        <p className="text-sm mt-2 font-medium">Les demandes du Chef de Train apparaîtront ici en temps réel.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4">
                        {detailedStops.map((stop, idx) => {
                            const reqObj = stopRequests.find(r => r.station_name.trim().toLowerCase() === stop.stationName.trim().toLowerCase());
                            const status = reqObj?.status || 'pending';

                            let cardBorderColor = 'border-blue-500/30';
                            let accentColor = 'bg-blue-500';
                            if (status === 'accepted') {
                                cardBorderColor = 'border-emerald-500/40';
                                accentColor = 'bg-emerald-500';
                            } else if (status === 'rejected') {
                                cardBorderColor = 'border-red-500/40';
                                accentColor = 'bg-red-500';
                            }

                            return (
                                <div
                                    key={`${stop.stationName}-${idx}`}
                                    className={`group relative overflow-hidden bg-slate-900/60 border ${cardBorderColor} rounded-[2rem] p-6 shadow-[0_10px_30px_rgba(0,0,0,0.3)] transition-all`}
                                >
                                    {/* Accent line */}
                                    <div className={`absolute inset-y-0 left-0 w-1.5 ${accentColor} transition-colors`} />

                                    <div className="flex justify-between items-start pl-2">
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <MapPin size={14} className="text-slate-400" />
                                                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Localisation</span>
                                            </div>
                                            <h3 className="text-2xl font-black uppercase tracking-tight text-white leading-none">
                                                {stop.stationName}
                                            </h3>
                                            <div className="flex items-center gap-4 pt-1">
                                                <div className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-full border border-white/5">
                                                    <Clock size={12} className="text-slate-400" />
                                                    <span className="text-xs font-mono font-bold text-slate-300">{stop.arrivalTime}</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-full border border-white/5">
                                                    <span className="text-[10px] font-black text-slate-500 uppercase">PK</span>
                                                    <span className="text-xs font-mono font-bold text-slate-300">{stop.pk.toFixed(3)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-2 items-end">
                                            {/* Badge d'état */}
                                            {status === 'accepted' && (
                                                <span className="px-3 py-1 bg-emerald-500/10 text-emerald-400 text-[9px] font-black rounded-full uppercase tracking-wider border border-emerald-500/20">
                                                    Arrêt Validé
                                                </span>
                                            )}
                                            {status === 'rejected' && (
                                                <span className="px-3 py-1 bg-red-500/10 text-red-400 text-[9px] font-black rounded-full uppercase tracking-wider border border-red-500/20">
                                                    Arrêt Refusé
                                                </span>
                                            )}
                                            {status === 'pending' && (
                                                <span className="px-3 py-1 bg-amber-500/10 text-amber-400 text-[9px] font-black rounded-full uppercase tracking-wider border border-amber-500/20 animate-pulse">
                                                    Attente Conducteur
                                                </span>
                                            )}

                                            {/* Boutons d'action pour le conducteur */}
                                            <div className="flex items-center gap-2 mt-2">
                                                <button
                                                    onClick={() => onReject(stop.stationName)}
                                                    className={`p-2.5 rounded-xl border flex items-center justify-center transition-all active:scale-95 ${
                                                        status === 'rejected'
                                                            ? 'bg-red-600 text-white border-red-500 shadow-md shadow-red-900/30'
                                                            : 'bg-slate-900 border-white/5 text-slate-400 hover:text-red-500 hover:border-red-500/20'
                                                    }`}
                                                    title="Refuser l'arrêt"
                                                >
                                                    <Ban size={16} />
                                                </button>
                                                <button
                                                    onClick={() => onAccept(stop.stationName)}
                                                    className={`p-2.5 rounded-xl border flex items-center justify-center transition-all active:scale-95 ${
                                                        status === 'accepted'
                                                            ? 'bg-emerald-600 text-white border-emerald-500 shadow-md shadow-emerald-900/30'
                                                            : 'bg-slate-900 border-white/5 text-slate-400 hover:text-emerald-500 hover:border-emerald-500/20'
                                                    }`}
                                                    title="Valider l'arrêt"
                                                >
                                                    <Check size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>

            {/* FOOTER */}
            <footer className="p-6 bg-slate-900/30 border-t border-white/5 shrink-0">
                <div className="flex items-center justify-center gap-2 text-slate-500">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                    <p className="text-[9px] font-bold uppercase tracking-[0.2em]">Synchronisation Nexus Rail Active</p>
                </div>
            </footer>
        </div>
    );
}
